import { CURRENCY_CONFIG_MAP } from "./constants";
import BigMath from "./math";
import { CurrencyCode, CurrencySymbolDisplay, FormatOptions } from "./types";

import { applyUnitScale, INDIAN_UNITS, INTERNATIONAL_UNITS } from "./scale";

/**
 * 辅助函数：根据分组规则格式化整数部分
 * @param integerPart 整数部分字符串
 * @param grouping 分组规则数组 (e.g. [3] for Western, [3, 2] for Indian)
 * @param separator 分隔符
 */
function formatWithGrouping( integerPart: string, grouping: number[], separator: string ) {
    let result = "";
    let index = integerPart.length;
    let groupIndex = 0;

    while ( index > 0 ) {
        const size = grouping[Math.min( groupIndex, grouping.length - 1 )];
        const start = Math.max( index - size, 0 );
        const chunk = integerPart.slice( start, index );

        result = result ? `${chunk}${separator}${result}` : chunk;
        index = start;
        groupIndex++;
    }

    return result;
}

function applyCurrencySymbolDisplay( value: string, symbol: string, display: CurrencySymbolDisplay, sign: string ) {
    if ( display === false ) return value;
    if ( display === "suffix" ) return `${value}${symbol}`;
    if ( display === "only" ) return symbol;

    return `${sign}${symbol}${value.slice( sign.length )}`;
}

function resolveDefaultCurrencySymbolDisplay(
    currency: CurrencyCode,
    display: CurrencySymbolDisplay,
): CurrencySymbolDisplay {
    if ( display !== true ) return display;
    return currency === "CENT" ? "suffix" : "prefix";
}

function trimTrailingZeros( value: string ) {
    if ( !value.includes( "." ) ) return value;
    return value.replace( /\.?0+$/, "" );
}

function getDecimalString( amount: number, options: FormatOptions ) {
    const { decimalDisplay, maxPrecision } = options;

    // 动态小数位仅在未传 precision 时生效，避免影响历史固定精度行为。
    if ( decimalDisplay === "unlimited" ) {
        return trimTrailingZeros( BigMath.BN( amount ).toFixed() );
    }

    if ( decimalDisplay === "max" && maxPrecision !== undefined ) {
        return trimTrailingZeros( BigMath.toFixed( amount, maxPrecision ) );
    }

    return undefined;
}

/**
 * 货币格式化器
 * 负责将数值转换为符合特定货币区域习惯的字符串
 */
export class CurrencyFormatter {
    /**
     * 格式化金额
     * @param amount 金额数值
     * @param currency 货币代码
     * @param options 配置选项
     * @param options.precision 强制指定小数位数 (若不指定则根据缩放结果或默认配置决定)
     * @param options.useGrouping 是否使用千分位分割 (默认 true)
     * @param options.useScale 是否使用单位缩放 (默认 true)
     */
    format( amount: number, currency: CurrencyCode, options?: FormatOptions ): string {
        const { precision, useGrouping = true, useScale = true, scaleMinAmount, showCurrencySymbol = true } = options
            || {};

        const config = CURRENCY_CONFIG_MAP[currency];
        if ( !config ) {
            console.warn( `Currency config for ${currency} not found.` );
            return `${amount}`;
        }

        // --- SCALING LOGIC START ---
        let displayAmount = amount;
        let unitSuffix = "";
        let defaultPrecision = config.decimalPlaces;

        const scaleThreshold = scaleMinAmount ?? 1_000_000;
        const absAmountInput = BigMath.abs( amount );
        if (
            useScale && config.unitSystem && config.unitSystem !== "NONE"
            && BigMath.gte( absAmountInput, scaleThreshold )
        ) {
            const units = config.unitSystem === "INDIAN" ? INDIAN_UNITS : INTERNATIONAL_UNITS;
            const scaled = applyUnitScale( amount, units );
            displayAmount = scaled.value;
            if ( scaled.unit ) {
                unitSuffix = ` ${scaled.unit}`;
                defaultPrecision = scaled.decimalPlaces;
            }
        }
        // --- SCALING LOGIC END ---

        const isNegative = BigMath.lt( displayAmount, 0 );
        const absAmount = BigMath.abs( displayAmount );
        const decimalPlaces = precision ?? defaultPrecision;

        // 默认逻辑保持原样：未启用新选项时，继续使用固定精度分支。
        let fixedString = BigMath.toFixed( absAmount, decimalPlaces );

        if ( precision === undefined ) {
            const dynamicFixedString = getDecimalString( absAmount, {
                decimalDisplay: options?.decimalDisplay,
                maxPrecision: options?.maxPrecision,
            } );

            if ( dynamicFixedString !== undefined ) {
                fixedString = dynamicFixedString;
            }
        }

        const [ integer, decimal ] = fixedString.split( "." );

        const formattedInt = useGrouping
            ? formatWithGrouping( integer, config.grouping, config.thousandSeparator )
            : integer;

        const sign = isNegative ? "-" : "";
        const decimalPart = decimal ? `${config.decimalSeparator}${decimal}` : "";
        const formattedValue = `${sign}${formattedInt}${decimalPart}${unitSuffix}`;
        const resolvedSymbolDisplay = resolveDefaultCurrencySymbolDisplay( currency, showCurrencySymbol );

        return applyCurrencySymbolDisplay( formattedValue, config.symbol, resolvedSymbolDisplay, sign );
    }
}
